import React from 'react';

export function isContactVar(row) {
  return String(row?.source || '').startsWith('contact:');
}

export function contactSourceShortLabel(source) {
  const map = {
    'contact:name': 'Name',
    'contact:company': 'Unternehmen',
    'contact:email': 'E-Mail',
    'contact:phone': 'Telefon',
    'contact:website': 'Website',
    'contact:notes': 'Notizen',
    'contact:anrede': 'Anrede',
  };
  return map[source] || String(source || '').replace(/^contact:/, '') || 'Kontakt';
}

/** Token `{{key}}` in ein Input/Textarea an der Cursorposition einfügen */
export function insertTokenIntoInput(el, currentValue, token, setValue) {
  const value = String(currentValue || '');
  if (!el || typeof el.selectionStart !== 'number') {
    setValue(value + token);
    return;
  }
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? value.length;
  const next = value.slice(0, start) + token + value.slice(end);
  setValue(next);
  requestAnimationFrame(() => {
    try {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    } catch (_) {
      /* ignore */
    }
  });
}

/** Token in ReactQuill-Editor an der Cursorposition einfügen */
export function insertTokenIntoQuill(quillRef, token) {
  const editor = quillRef?.current?.getEditor?.();
  if (!editor) return false;
  const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
  editor.insertText(range.index, token, 'user');
  editor.setSelection(range.index + token.length, 0, 'user');
  return true;
}

/**
 * Kompakte Variablen-Leiste zum Einfügen von {{platzhaltern}}.
 */
export default function VariableInsertPanel({
  variables = [],
  insertTarget = 'content',
  onInsert,
  className = '',
}) {
  if (!variables.length) return null;

  const groups = [
    {
      id: 'contact',
      title: 'Aus Kontakt',
      hint: 'Wert vom Empfänger-Kontakt',
      items: variables.filter(isContactVar),
    },
    {
      id: 'custom',
      title: 'Fester Text',
      hint: 'Immer derselbe Wert',
      items: variables.filter((v) => !isContactVar(v)),
    },
  ].filter((g) => g.items.length > 0);

  if (!groups.length) return null;

  return (
    <div
      className={`rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/50 p-2.5 space-y-2.5 ${className}`}
    >
      <p className="text-[11px] text-neutral-500 dark:text-neutral-400">
        Variable einfügen in{' '}
        <strong className="text-neutral-700 dark:text-neutral-200">
          {insertTarget === 'subject' ? 'Betreff' : 'Nachricht'}
        </strong>
        {' · '}
        wird beim Senden ersetzt
      </p>
      {groups.map((group) => (
        <div key={group.id} className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-300">
              {group.title}
            </p>
            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 truncate">
              {group.hint}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {group.items.map((v) => (
              <button
                key={v.id || v.key}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onInsert?.(v.key)}
                className="inline-flex flex-col items-start gap-0.5 px-2 py-1.5 text-left rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:border-primary-400 transition-colors"
                title={
                  isContactVar(v)
                    ? contactSourceShortLabel(v.source)
                    : v.value
                      ? String(v.value)
                      : 'Fester Text'
                }
              >
                <span className="text-xs font-mono text-primary-700 dark:text-primary-300">
                  {`{{${v.key}}}`}
                </span>
                <span className="text-[10px] text-neutral-400 dark:text-neutral-500 leading-tight max-w-[9rem] truncate">
                  {isContactVar(v)
                    ? contactSourceShortLabel(v.source)
                    : v.value
                      ? String(v.value).slice(0, 28)
                      : 'Fester Text'}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
